/**
 * Flat Rate Pricing Types and Utilities
 *
 * This module provides types and functions for zone-based flat rate pricing.
 * It enables checking if pickup/dropoff locations fall within defined geographic
 * zones and matching them to flat rate pricing rules.
 */

// =============================================================================
// TYPES
// =============================================================================

/**
 * Coordinate pair in GeoJSON order: [longitude, latitude]
 * Note: This is different from Google Maps which uses (lat, lng)
 */
export type Coordinate = [number, number]

/**
 * A point with latitude and longitude (Google Maps style)
 */
export interface LatLng {
  lat: number
  lng: number
}

/**
 * Bounding box for quick spatial filtering
 */
export interface BoundingBox {
  minLng: number
  minLat: number
  maxLng: number
  maxLat: number
}

/**
 * A geographic zone defined by a polygon
 */
export interface FlatRateZone {
  id: string
  name: string
  description?: string | null
  polygon: Coordinate[]
  boundingBox?: BoundingBox | null
  color: string
  isActive: boolean
}

/**
 * Direction of the flat rate override
 * - 'directional': Only applies in one direction (pickup zone -> dropoff zone)
 * - 'bidirectional': Applies in both directions (A to B or B to A)
 */
export type FlatRateDirection = 'directional' | 'bidirectional'

/**
 * A flat rate pricing override rule
 */
export interface FlatRateOverride {
  id: string
  name: string
  description?: string | null
  pickupZone: FlatRateZone
  dropoffZone: FlatRateZone
  direction: FlatRateDirection
  flatRate: number
  vehicleIds: string[] | null // null = applies to all vehicles
  priority: number
  isActive: boolean
}

/**
 * Result of checking for a flat rate match
 */
export interface FlatRateMatch {
  override: FlatRateOverride
  matchedPickupZone: FlatRateZone
  matchedDropoffZone: FlatRateZone
  flatRate: number
  /** Whether the match was in the reverse direction (for bidirectional overrides) */
  isReversed: boolean
}

// =============================================================================
// BOUNDING BOX UTILITIES
// =============================================================================

/**
 * Calculate the bounding box from polygon coordinates.
 * Returns undefined if the polygon has no points.
 *
 * @param polygon - Array of [lng, lat] coordinate pairs
 * @returns Bounding box or undefined for empty polygons
 */
export function calculateBoundingBox(
  polygon: Coordinate[]
): BoundingBox | undefined {
  if (!polygon || polygon.length === 0) {
    return undefined
  }

  let minLng = Infinity
  let maxLng = -Infinity
  let minLat = Infinity
  let maxLat = -Infinity

  for (const [lng, lat] of polygon) {
    if (lng < minLng) minLng = lng
    if (lng > maxLng) maxLng = lng
    if (lat < minLat) minLat = lat
    if (lat > maxLat) maxLat = lat
  }

  return { minLng, minLat, maxLng, maxLat }
}

/**
 * Check if a point is within a bounding box.
 * This is a quick filter to use before the more expensive polygon check.
 *
 * @param point - Point with lat and lng
 * @param bbox - Bounding box to check
 * @returns true if point is inside or on the boundary
 */
export function isPointInBoundingBox(
  point: LatLng,
  bbox: BoundingBox | null | undefined
): boolean {
  if (!bbox) {
    // No bounding box means we can't filter, so return true to check polygon
    return true
  }

  const { lat, lng } = point
  return (
    lng >= bbox.minLng &&
    lng <= bbox.maxLng &&
    lat >= bbox.minLat &&
    lat <= bbox.maxLat
  )
}

// =============================================================================
// POLYGON UTILITIES
// =============================================================================

/**
 * Check if a point is inside a polygon using the ray casting algorithm.
 *
 * Algorithm: Cast a ray from the point to infinity and count how many times
 * it crosses the polygon boundary. If odd, the point is inside.
 *
 * Reference: https://en.wikipedia.org/wiki/Point_in_polygon#Ray_casting_algorithm
 *
 * @param point - Point with lat and lng (Google Maps style)
 * @param polygon - Array of [lng, lat] coordinate pairs (GeoJSON style)
 * @returns true if the point is inside the polygon
 */
export function isPointInPolygon(point: LatLng, polygon: Coordinate[]): boolean {
  if (!polygon || polygon.length < 3) {
    return false
  }

  const { lat: y, lng: x } = point
  let inside = false

  // Get the number of vertices, handling closed polygons
  const n = polygon.length
  const isClosed =
    polygon[0][0] === polygon[n - 1][0] && polygon[0][1] === polygon[n - 1][1]
  const vertexCount = isClosed ? n - 1 : n

  for (let i = 0, j = vertexCount - 1; i < vertexCount; j = i++) {
    const xi = polygon[i][0] // lng
    const yi = polygon[i][1] // lat
    const xj = polygon[j][0]
    const yj = polygon[j][1]

    // Check if the ray from point (x, y) going right intersects edge (i, j)
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi

    if (intersect) {
      inside = !inside
    }
  }

  return inside
}

/**
 * Check if a point is inside a zone (uses bounding box for quick filtering).
 *
 * @param point - Point with lat and lng
 * @param zone - Flat rate zone with polygon and optional bounding box
 * @returns true if the point is inside the zone
 */
export function isPointInZone(point: LatLng, zone: FlatRateZone): boolean {
  // Quick filter with bounding box
  if (!isPointInBoundingBox(point, zone.boundingBox)) {
    return false
  }

  // Full polygon check
  return isPointInPolygon(point, zone.polygon)
}

// =============================================================================
// FLAT RATE MATCHING
// =============================================================================

/**
 * Check if a single flat rate override matches the given pickup and dropoff.
 *
 * @param pickup - Pickup location
 * @param dropoff - Dropoff location
 * @param vehicleId - Vehicle ID to check against
 * @param override - Flat rate override to check
 * @returns FlatRateMatch if matched, null otherwise
 */
function checkOverrideMatch(
  pickup: LatLng,
  dropoff: LatLng,
  vehicleId: string,
  override: FlatRateOverride
): FlatRateMatch | null {
  // Check vehicle scope
  if (override.vehicleIds !== null && !override.vehicleIds.includes(vehicleId)) {
    return null
  }

  // Check forward direction: pickup in pickup zone, dropoff in dropoff zone
  if (
    isPointInZone(pickup, override.pickupZone) &&
    isPointInZone(dropoff, override.dropoffZone)
  ) {
    return {
      override,
      matchedPickupZone: override.pickupZone,
      matchedDropoffZone: override.dropoffZone,
      flatRate: override.flatRate,
      isReversed: false,
    }
  }

  // Check reverse direction for bidirectional overrides
  if (override.direction === 'bidirectional') {
    if (
      isPointInZone(pickup, override.dropoffZone) &&
      isPointInZone(dropoff, override.pickupZone)
    ) {
      return {
        override,
        matchedPickupZone: override.dropoffZone, // Reversed
        matchedDropoffZone: override.pickupZone, // Reversed
        flatRate: override.flatRate,
        isReversed: true,
      }
    }
  }

  return null
}

/**
 * Find matching flat rate override for a trip.
 *
 * Returns the highest priority (lowest priority number) match, or null if no match.
 * At the same priority level, vehicle-specific overrides take precedence over
 * overrides that apply to all vehicles.
 *
 * @param pickup - Pickup location with lat and lng
 * @param dropoff - Dropoff location with lat and lng
 * @param vehicleId - Vehicle ID to check
 * @param overrides - Array of flat rate overrides (should be pre-filtered to active only)
 * @returns FlatRateMatch if found, null otherwise
 */
export function findFlatRateMatch(
  pickup: LatLng,
  dropoff: LatLng,
  vehicleId: string,
  overrides: FlatRateOverride[]
): FlatRateMatch | null {
  if (!overrides || overrides.length === 0) {
    return null
  }

  // Sort by priority (lower = higher priority), then by vehicle-specificity
  const sortedOverrides = [...overrides].sort((a, b) => {
    // First sort by priority (lower number = higher priority)
    if (a.priority !== b.priority) {
      return a.priority - b.priority
    }
    // At same priority, vehicle-specific takes precedence
    const aIsSpecific = a.vehicleIds !== null
    const bIsSpecific = b.vehicleIds !== null
    if (aIsSpecific !== bIsSpecific) {
      return aIsSpecific ? -1 : 1
    }
    return 0
  })

  // Find first matching override
  for (const override of sortedOverrides) {
    // Skip inactive overrides
    if (!override.isActive) {
      continue
    }

    const match = checkOverrideMatch(pickup, dropoff, vehicleId, override)
    if (match) {
      return match
    }
  }

  return null
}

// =============================================================================
// TRANSFORMATION HELPERS
// =============================================================================

/**
 * Transform database row to FlatRateZone type.
 *
 * @param row - Database row from flat_rate_zones table
 * @returns FlatRateZone object
 */
export function transformZoneFromDb(row: {
  id: string
  name: string
  description?: string | null
  polygon: unknown
  bounding_box?: unknown
  color: string
  is_active: boolean
}): FlatRateZone {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    polygon: row.polygon as Coordinate[],
    boundingBox: row.bounding_box as BoundingBox | null,
    color: row.color,
    isActive: row.is_active,
  }
}

/**
 * Transform database row with joined zones to FlatRateOverride type.
 *
 * @param row - Database row from flat_rate_overrides with joined zones
 * @returns FlatRateOverride object
 */
export function transformOverrideFromDb(row: {
  id: string
  name: string
  description?: string | null
  pickup_zone: {
    id: string
    name: string
    description?: string | null
    polygon: unknown
    bounding_box?: unknown
    color: string
    is_active: boolean
  }
  dropoff_zone: {
    id: string
    name: string
    description?: string | null
    polygon: unknown
    bounding_box?: unknown
    color: string
    is_active: boolean
  }
  direction: string
  flat_rate: number
  vehicle_ids: string[] | null
  priority: number
  is_active: boolean
}): FlatRateOverride {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    pickupZone: transformZoneFromDb(row.pickup_zone),
    dropoffZone: transformZoneFromDb(row.dropoff_zone),
    direction: row.direction as FlatRateDirection,
    flatRate: row.flat_rate,
    vehicleIds: row.vehicle_ids,
    priority: row.priority,
    isActive: row.is_active,
  }
}
